import { Link } from "react-router-dom";
import IndustriesServed from "./IndustriesServedHome";
import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger"; 

import KeyDifferentiators from "../components/Sections/KeyDifferentiators";
import HowWeWork from "../components/HowWeWork";
import AiDevProcess from "../components/Sections/AiDevProcess";


gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const industriesRef = useRef(null);

  useGSAP(() => {
    // Hero Animations
    gsap.fromTo(heroRef.current.querySelector('.hero-title'), 
      { y: 50, opacity: 0 }, 
      { y: 0, opacity: 1, duration: 0.6, ease: "power2.out", delay: 0.1 } 
    );
    gsap.fromTo(heroRef.current.querySelector('.hero-subtitle'), 
      { y: 30, opacity: 0 }, 
      { y: 0, opacity: 1, duration: 0.4, ease: "power2.out", delay: 0.2 } 
    );
    gsap.fromTo(heroRef.current.querySelectorAll('.hero-button'), 
      { scale: 0.9, opacity: 0 }, 
      { scale: 1, opacity: 1, duration: 0.3, ease: "power2.out", stagger: 0.1, delay: 0.4 } 
    );

    // Services Title Animation
    gsap.fromTo(servicesRef.current.querySelector('.services-title'),
      { y: 40, opacity: 0 }, 
      {
        y: 0,
        opacity: 1,
        duration: 0.3, 
        ease: "power2.out",
        scrollTrigger: {
          trigger: servicesRef.current,
          start: "top 85%", // Adjusted trigger for better windows scrolling
          end: "top 50%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Service Cards Animation
    gsap.fromTo(servicesRef.current.querySelectorAll('.service-card'),
      { y: 30, opacity: 0 }, 
      {
        y: 0,
        opacity: 1,
        duration: 0.4, 
        ease: "power2.out",
        stagger: 0.08, 
        scrollTrigger: {
          trigger: servicesRef.current.querySelector('.services-grid'),
          start: "top 85%",
          end: "top 40%",
          toggleActions: "play none none reverse"
        }
      }
    );
  }, []);

  // const handleServiceHover = (e, isHover) => {
  //   const card = e.currentTarget;
  //   const icon = card.querySelector('.service-icon');
    
  //   if (isHover) {
  //     gsap.to(card, { y: -5, duration: 0.1, ease: "power2.out" }); 
  //     gsap.to(icon, { scale: 1.1, duration: 0.2, ease: "power2.out" }); 
  //   } else {
  //     gsap.to(card, { y: 0, duration: 0.1, ease: "power2.out" }); // Reset to 0 instead of -1
  //     gsap.to(icon, { scale: 1, duration: 0.2, ease: "power2.out" });
  //   }
  // };

  return (
    <div className="relative antialiased selection:bg-[#952877] selection:text-white">
      
     
      <main ref={heroRef} className="min-h-[100dvh] w-full flex items-center justify-center text-center relative overflow-hidden bg-white py-20">
        
        {/* Layered Background Glows */}
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-[#952877]/10 blur-[120px] rounded-full"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] bg-[#592aa7]/10 blur-[150px] rounded-full"></div>
        
        {/* Technical Grid Overlay */}
        {/* <div className="absolute inset-0 opacity-[0.05] pointer-events-none" 
          style={{ backgroundImage: `linear-gradient(#17091a 1px, transparent 1px), linear-gradient(90deg, #17091a 1px, transparent 1px)`, backgroundSize: '60px 60px' }}>
        </div> */}

        <div className="max-w-6xl mx-auto px-6 relative z-10 flex flex-col items-center justify-center h-full">
          


          {/* 
            FIX 2: Tamed the Headline Size 
            Old: text-[clamp(4rem,10vw,8.5rem)] -> Massive on 125% scaled screens
            New: text-5xl md:text-7xl lg:text-8xl -> Controlled breakpoints
          */}
          <h1 className="hero-title text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-[1.1] md:leading-[0.95] tracking-tighter text-[#17091a]">
            Transform <br /> 
            <span className="bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#17091a] bg-clip-text text-transparent  pr-2 md:pr-4">
              Business
            </span>

            <br />
            <span className="text-[0.6em] md:text-[0.5em] lg:text-[0.45em] tracking-normal font-bold block mt-2">with AI Excellence</span>
          </h1>

          <p className="hero-subtitle text-lg md:text-xl text-gray-500 mb-12 max-w-xl md:max-w-2xl mx-auto leading-relaxed font-light">
            ViconAI creates <span className="text-[#17091a] font-semibold italic">“new to the world”</span> products that unlock high-scale opportunities through custom neural architecture.
          </p>

          {/* CTA Group */}
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center items-center w-full sm:w-auto">
            <Link 
              to="/contact" 
              className="hero-button group relative px-8 sm:px-12 py-5 sm:py-6 bg-[#17091a] text-white rounded-2xl font-bold text-base sm:text-lg overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-[0_30px_60px_-15px_rgba(149,40,119,0.4)] w-full sm:w-auto"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#952877] to-[#592aa7] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <span className="relative z-10">Start Your Transformation</span>
            </Link>

            <Link
              to="/Services"
              className="hero-button group flex items-center justify-center px-6 py-2 text-[#17091a] font-bold text-lg hover:text-[#952877] transition-all duration-300 relative w-full sm:w-auto"
            >
              Explore Our Ecosystem
              <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#952877]/30 group-hover:w-full transition-all duration-500"></div>
            </Link>
          </div>
        </div>
      </main>

      <HowWeWork />

      {/* Core Services Preview */}
      <section ref={servicesRef} className="py-20 md:py-24 bg-white relative overflow-hidden">
        {/* Soft Decorative Background Glows */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#592aa7]/5 blur-[120px] rounded-full pointer-events-none"></div>

        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <h2 className="services-title text-3xl md:text-7xl font-extrabold text-center mb-6 text-[#17091a]">
            Our Core <span className="bg-gradient-to-r from-[#952877] to-[#592aa7] bg-clip-text text-transparent">Services</span>
          </h2>
          <p className="text-lg md:text-xl text-center mb-16 text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Scalable AI execution designed for high-impact business transformation.
          </p>
          

          <div className="services-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            {[
              {
                title: "AI Consultation & Advisory",
                desc: "Strategic roadmaps that align AI initiatives with business objectives, ensuring maximum ROI and competitive advantage.",
                image: "/images/ai_strategy.png", 
                features: [
                  "AI Readiness Assessment",
                  "Technology Stack Selection", 
                  "ROI Optimization"
                ]
              },
              {
                title: "Data Intelligence & Analytics",
                desc: "Turn raw data into business insights with advanced analytics and real-time decision tools.",
                image: "/images/ai_transformation.png",
                features: [
                  "Unified data lakes & warehouses",
                  "Predictive modeling",
                  "Interactive dashboards",
                  "Automated machine learning"
                ]
              },
              {
                title: "Custom AI Solutions",
                desc: "Partnership-driven approach where your team gains AI expertise while we deliver cutting-edge solutions.",
                image: "/images/collaborative_development.png", 
                features: [
                  "Team Augmentation", 
                  "Knowledge Transfer",
                  "Ongoing Support"
                ]
              }
            ].map((service, index) => (
              <div
                key={index}
                className="service-card group p-8 md:p-10 rounded-[2rem] bg-white border border-gray-100 shadow-sm hover:shadow-[0_20px_50px_-12px_rgba(89,42,167,0.15)] transition-all duration-500 cursor-pointer relative overflow-hidden flex flex-col h-full"
                onMouseEnter={(e) => handleServiceHover(e, true)}
                onMouseLeave={(e) => handleServiceHover(e, false)}>
                
                <div className="absolute inset-0 bg-gradient-to-br from-[#952877]/5 to-[#592aa7]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="mb-6 md:mb-8 relative z-10">
                  <div className="service-icon w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-white transition-all duration-500 shadow-inner group-hover:shadow-md">
                    <img 
                      src={service.image}
                      className="w-8 h-8 md:w-10 md:h-10 object-contain" 
                      alt={service.title}/>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#17091a] leading-tight group-hover:text-[#952877] transition-colors duration-300">
                    {service.title}
                  </h3>
                </div>
                
                <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8 relative z-10 leading-relaxed flex-grow">
                  {service.desc}
                </p>
                
                <ul className="space-y-3 relative z-10">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm font-medium text-gray-700">
                      <span className="w-2 h-2 bg-gradient-to-r from-[#952877] to-[#592aa7] rounded-full mr-3 flex-shrink-0"></span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="absolute bottom-0 left-0 w-full h-1.5 bg-gray-100">
                  <div className="h-full bg-gradient-to-r from-[#952877] to-[#592aa7] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-16 md:mt-20 text-center relative z-10">
            <Link 
              to="/services" 
              className="inline-flex items-center text-[#592aa7] hover:text-[#952877] transition-all text-lg md:text-xl font-bold group">
              Explore All Services
              <div className="ml-3 p-2 rounded-full bg-gray-50 group-hover:bg-[#952877]/10 transition-colors">
                <svg 
                  className="w-6 h-6 group-hover:translate-x-1 transition-transform" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2.5" 
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  ></path>
                </svg>
              </div>
            </Link>
          </div>
        </div> */}
      </section>

      <KeyDifferentiators />
      
      <div ref={industriesRef}>
        <IndustriesServed />
      </div>

      <AiDevProcess />
       
    </div>
  );
};

export default Home;
